import Layout from "@/components/Layout";
import {
  getAllAccuracy,
  LEAD_TIME_BUCKETS,
  type AccuracyStats,
  type RegionAccuracy,
} from "@/lib/forecast-accuracy";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

/**
 * Forecast-accuracy dashboard.
 *
 * A minimal read-only view over `/api/forecast-accuracy`. Not linked
 * from nav — reached by typing `/accuracy` directly. Good enough for
 * ops/monitoring until there's a reason to make it prettier.
 */
export default function Accuracy() {
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["forecast-accuracy", "all"],
    queryFn: getAllAccuracy,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Forecast accuracy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          MAE in meters, bias in meters (positive = forecast over-predicts wave height).
          Only rows with a paired buoy observation contribute; the 0h bucket is the
          nowcast, longer leads fill in after the back-fill cron runs.
        </Typography>

        {isLoading && (
          <Typography sx={{ mt: 4 }} color="text.secondary">
            Loading…
          </Typography>
        )}
        {isError && (
          <Typography sx={{ mt: 4 }} color="error">
            Failed to load accuracy stats.
          </Typography>
        )}
        {!isLoading && !isError && data.length === 0 && (
          <Typography sx={{ mt: 4 }} color="text.secondary">
            No accuracy data yet. Come back once some forecasts have been logged and the
            back-fill cron has run.
          </Typography>
        )}
        {data.length > 0 && <AccuracyTable rows={data} />}
      </Container>
    </Layout>
  );
}

function AccuracyTable({ rows }: { rows: RegionAccuracy[] }) {
  // Sort regions by overall sample count descending — the regions with
  // the most data are the most interesting ones to look at first.
  const sorted = [...rows].sort(
    (a, b) => (b.overall.sampleCount ?? 0) - (a.overall.sampleCount ?? 0),
  );

  return (
    <TableContainer sx={{ mt: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2}>Region</TableCell>
            <TableCell align="center" colSpan={3}>
              Overall
            </TableCell>
            {LEAD_TIME_BUCKETS.map((b) => (
              <TableCell key={b} align="center" colSpan={3}>
                {b}h
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {/* Sub-header row — repeat the triplet per bucket */}
            <MaeHeaderCells />
            {LEAD_TIME_BUCKETS.map((b) => (
              <MaeHeaderCells key={b} />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.regionId} hover>
              <TableCell sx={{ fontFamily: "monospace" }}>{r.regionId}</TableCell>
              <StatsCells stats={r.overall} />
              {LEAD_TIME_BUCKETS.map((b) => (
                <StatsCells key={b} stats={r.byLeadTime[String(b)]} />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MaeHeaderCells() {
  return (
    <>
      <TableCell align="right" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
        n
      </TableCell>
      <TableCell align="right" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
        MAE
      </TableCell>
      <TableCell align="right" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
        bias
      </TableCell>
    </>
  );
}

function StatsCells({ stats }: { stats: AccuracyStats | undefined }) {
  if (!stats) return <EmptyStatsCells />;
  const dim = stats.sampleCount === 0;
  return (
    <>
      <TableCell align="right" sx={{ opacity: dim ? 0.3 : 1 }}>
        {stats.sampleCount}
      </TableCell>
      <TableCell align="right" sx={{ opacity: dim ? 0.3 : 1 }}>
        {formatNumber(stats.waveHeightMAE)}
      </TableCell>
      <TableCell align="right" sx={{ opacity: dim ? 0.3 : 1 }}>
        {formatNumber(stats.bias)}
      </TableCell>
    </>
  );
}

function EmptyStatsCells() {
  return (
    <>
      <TableCell align="right" sx={{ opacity: 0.3 }}>
        —
      </TableCell>
      <TableCell align="right" sx={{ opacity: 0.3 }}>
        —
      </TableCell>
      <TableCell align="right" sx={{ opacity: 0.3 }}>
        —
      </TableCell>
    </>
  );
}

function formatNumber(n: number | null): string {
  if (n == null) return "—";
  return n.toFixed(2);
}
